/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { baseStyles, skeletonStyles } from '@/styles';

@customElement('fg-wholesale-configurator-skeleton-loader')
export class FgWholesaleConfiguratorSkeletonLoader extends LitElement {
  render() {
    return html`
      <div class="skeleton-grid">
        <div class="skeleton-left-column">
          <!-- Step 1: Select Articles -->
          <div class="skeleton-step">
            <div class="skeleton-step-header">
              <div class="skeleton-step-number"></div>
              <div class="skeleton-step-title"></div>
            </div>
            <div class="skeleton-step-content">
              ${Array.from({ length: 2 }).map(
                () => html`
                  <div class="skeleton-product-card">
                    <div class="skeleton-product-top">
                      <div class="skeleton-product-image"></div>
                      <div class="skeleton-product-info">
                        <div class="skeleton-product-name"></div>
                        <div class="skeleton-product-code"></div>
                      </div>
                    </div>
                    <div class="skeleton-product-middle">
                      <div class="skeleton-product-price"></div>
                      <div class="skeleton-product-quantity"></div>
                    </div>
                    <div class="skeleton-product-bottom">
                      <div class="skeleton-product-stock">
                        <div class="skeleton-stock-icon"></div>
                        <div class="skeleton-stock-value"></div>
                      </div>
                      <div class="skeleton-product-backorder">
                        <div class="skeleton-backorder-text"></div>
                        <div class="skeleton-backorder-link"></div>
                      </div>
                    </div>
                  </div>
                `,
              )}
            </div>
          </div>

          <!-- Step 2: Select Print Options -->
          <div class="skeleton-step">
            <div class="skeleton-step-header">
              <div class="skeleton-step-number"></div>
              <div class="skeleton-step-title"></div>
            </div>
            <div class="skeleton-step-content">
              ${Array.from({ length: 2 }).map(
                () => html`
                  <div class="skeleton-print-option-card">
                    <div class="skeleton-print-option-image"></div>

                    <div class="skeleton-print-option-content">
                      <div class="skeleton-print-option-label"></div>
                      <div class="skeleton-print-option-dropdown"></div>
                      <div class="skeleton-print-option-end-spacer"></div>
                    </div>
                  </div>
                `,
              )}
            </div>
          </div>
        </div>

        <!-- Right Column: Summary -->
        <div class="skeleton-right-column">
          <div class="skeleton-summary">
            <div class="skeleton-summary-title"></div>
            <div class="skeleton-summary-price-row">
              <div class="skeleton-price-label"></div>
              <div class="skeleton-price-value"></div>
            </div>
            <div class="skeleton-price-details-link">
              <div class="skeleton-link-text"></div>
            </div>
            <div class="skeleton-add-to-cart-button"></div>
            <div class="skeleton-save-link">
              <div class="skeleton-link-text"></div>
            </div>
          </div>
        </div>

        <!-- Mobile: Start Ordering Button -->
        <div class="skeleton-mobile-button"></div>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      :host {
        --skeleton-gap-default: var(--skeleton-gap-large);
      }

      .skeleton-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--skeleton-gap-default);
        width: 100%;
      }

      .skeleton-left-column {
        grid-column: span 3;
        display: none;
        flex-direction: column;
        gap: var(--skeleton-gap-default);
      }

      .skeleton-right-column {
        display: none;
      }

      .skeleton-mobile-button {
        display: block;
        width: 100%;
        height: 3.5rem;
        background: var(--skeleton-gray);
        border-radius: 9999px;
        margin: 1rem 0;
      }

      .skeleton-step {
        background-color: var(--wholesale-skeleton-bg);
        border-radius: var(--skeleton-border-radius-lg);
        width: 100%;
      }

      .skeleton-step-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;
      }

      .skeleton-step-number {
        width: 2rem;
        height: 2rem;
        border-radius: var(--skeleton-border-radius-lg);
        background: var(--skeleton-gray);
        flex-shrink: 0;
      }

      .skeleton-step-title {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.5rem;
        width: 14rem;
      }

      .skeleton-step-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .skeleton-product-card {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
        background-color: var(--skeleton-gray-light);
        border-radius: var(--skeleton-border-radius);
      }

      .skeleton-product-top {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
      }

      .skeleton-product-image {
        width: 60px;
        height: 60px;
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        flex-shrink: 0;
      }

      .skeleton-product-info {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        flex: 1;
      }

      .skeleton-product-name {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.25rem;
        width: 8rem;
      }

      .skeleton-product-code {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 12rem;
        opacity: 0.7;
      }

      .skeleton-product-middle {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .skeleton-product-price {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.25rem;
        width: 5rem;
      }

      .skeleton-product-quantity {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 46px;
        width: 7rem;
      }

      .skeleton-product-bottom {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .skeleton-product-stock {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .skeleton-stock-icon {
        width: 16px;
        height: 16px;
        border-radius: var(--skeleton-border-radius);
        background: var(--skeleton-gray);
        flex-shrink: 0;
      }

      .skeleton-stock-value {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 3rem;
      }

      .skeleton-product-backorder {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .skeleton-backorder-text {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 0.875rem;
        width: 6rem;
        opacity: 0.7;
      }

      .skeleton-backorder-link {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 5rem;
        opacity: 0.6;
      }

      .skeleton-print-option-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        padding: 8px;
        background-color: var(--skeleton-gray-light);
        border-radius: var(--skeleton-border-radius);
      }

      .skeleton-print-option-label {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.25rem;
        width: 10rem;
      }

      .skeleton-print-option-dropdown {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 2.5rem;
        width: 12rem;
      }

      .skeleton-right-column {
        grid-column: span 3;
        margin-top: 0;
      }

      .skeleton-summary {
        background-color: var(--skeleton-gray-light);
        border-radius: var(--skeleton-border-radius-lg);
        padding: 1rem;
        height: fit-content;
      }

      .skeleton-summary-title {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1.5rem;
        width: 8rem;
        margin-bottom: 1.5rem;
        display: none;
      }

      .skeleton-summary-price-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
      }

      .skeleton-price-label {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 6rem;
      }

      .skeleton-price-value {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 5rem;
      }

      .skeleton-price-details-link {
        justify-content: flex-end;
      }

      .skeleton-save-link {
        justify-content: center;
      }

      .skeleton-price-details-link,
      .skeleton-save-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .skeleton-link-icon {
        width: 16px;
        height: 16px;
        border-radius: var(--skeleton-border-radius);
        background: var(--skeleton-gray);
        flex-shrink: 0;
      }

      .skeleton-link-text {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 10rem;
        opacity: 0.7;
      }

      .skeleton-add-to-cart-button {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius-lg);
        height: 3rem;
        width: 100%;
        margin-bottom: 1rem;
      }

      .skeleton-print-option-image {
        width: 48px;
        height: 48px;
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
      }

      .skeleton-print-option-spacer {
        width: 96px;
        height: 96px;
      }

      .skeleton-print-option-end-spacer {
        width: 200px;
        height: 96px;
        display: none;
      }

      .skeleton-print-option-content {
        display: none;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-print-option-content {
          display: flex;
          flex-direction: column;
        }

        .skeleton-print-option-label {
          margin-top: 6px;
          margin-bottom: 8px;
        }

        .skeleton-print-option-dropdown {
          margin-bottom: 8px;
        }
        .skeleton-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .skeleton-left-column {
          grid-column: span 2;
          display: block;
        }

        .skeleton-left-column,
        .skeleton-right-column {
          display: flex;
        }

        .skeleton-mobile-button {
          display: none;
        }

        .skeleton-summary {
          width: 375px;
          padding: 1rem 1.5rem;
        }

        .skeleton-right-column {
          grid-column: span 1;
          margin-top: 3rem;
        }

        .skeleton-summary-title {
          display: block;
        }

        .skeleton-summary {
          position: sticky;
          top: 1rem;
        }

        .skeleton-product-card {
          flex-direction: column;
        }

        .skeleton-product-middle {
          justify-content: space-between;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .skeleton-print-option-card {
          justify-content: flex-start;
        }
        .skeleton-print-option-content {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 20px;
        }

        .skeleton-print-option-image {
          width: 96px;
          height: 96px;
        }

        .skeleton-print-option-label {
          margin-top: 0px;
          margin-bottom: 0px;
        }

        .skeleton-print-option-dropdown {
          margin-bottom: 0px;
        }

        .skeleton-print-option-end-spacer {
          display: none;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .skeleton-product-card {
          flex-direction: row;
          align-items: center;
        }

        .skeleton-product-stock {
          display: none;
        }

        .skeleton-backorder-text {
          display: none;
        }

        .skeleton-product-info {
          align-self: center;
        }

        .skeleton-product-quantity {
          margin-right: 120px;
        }

        .skeleton-product-top {
          flex: 1;
        }

        .skeleton-product-middle {
          flex-direction: row;
          gap: 1rem;
        }

        .skeleton-product-bottom {
          flex-direction: row;
          align-items: center;
          gap: 1rem;
        }

        .skeleton-product-backorder {
          flex-direction: row;
          gap: 1rem;
        }
      }
    `,
  ];
}
